import ApiService from "../../services/ApiService";
import Logger from "../../services/Logger";
import { resolveApiFetchUrl } from "../../services/ApiBaseUrl";
import { apiFetch } from "../../services/apiFetch";
import { pickSlidingCardImageUrl, pickSlidingPlayImageUrl, getSlidingStoryTitle } from "../browse-sliding/slidingBrowseUtils";
import { pickJigsawCardImageUrl, pickJigsawSquareImageUrl } from "../puzzles/jigsaw/jigsawBrowseUtils";
import JigsawService, { normalizeHomeJigsawGroup } from "../puzzles/jigsaw/JigsawService";
import SlidingService from "../puzzles/sliding/SlidingService";
import FALLBACK_JIGSAW_PIECE_PATHS from "../../admin/test/jigsawPiecePaths-8.json";
import {
    pickDefaultJigsawPlayTemplate,
    resolveJigsawTemplateTileSize,
} from "../../prototype/mobile-jigsaw/lib/jigsawScreenshotUtils";
import {
    RECORDING_CONTENT_STORY_ID,
} from "../../prototype/mobile-jigsaw/recordingContentPuzzle";
import {
    DEMO_WORD_WHEEL_FOUND,
    DEMO_WORD_WHEEL_PUZZLE,
} from "./LandingWordWheelPlayPreview";

const PUBLIC = process.env.PUBLIC_URL || "";
const FETCH_MS = 8000;
const FALLBACK_JIGSAW_GRID = 8;
/** Homepage Sliding play preview — same board as `/puzzle/play-sliding/{story}/board/{id}`. */
const HERO_SLIDING_STORY_ID = "40c05049-f635-447d-b047-223c951cd329";
const HERO_SLIDING_ID = "7da2f523-4ad0-4ffe-bab2-bfa05fe2117c";
/** Homepage Word Wheel play preview — always this published template. */
const HERO_WORD_WHEEL_TPL_ID = "b96df8f8-b1c6-4fb2-a73f-30d0f3477ef2";
const DEMO_SLIDING_LAYOUT_4 = [4, 0, 2, 3, 8, 1, 6, 7, 12, 5, 10, 11, 13, 9, 14, 15];

function shuffledPieceIndices(count) {
    const pieces = Array.from({ length: Math.max(0, count) }, (_, i) => i);
    for (let i = pieces.length - 1; i > 0; i -= 1) {
        const j = Math.floor(Math.random() * (i + 1));
        const swap = pieces[i];
        pieces[i] = pieces[j];
        pieces[j] = swap;
    }
    return pieces;
}

/** Start empty: every piece is in the tray (shuffled) and flies home one by one. */
function layoutForGrid(grid) {
    const total = grid > 0 ? grid * grid : 0;
    return {
        boardPieceIndices: [],
        trayPieceIndices: shuffledPieceIndices(total),
    };
}

function dieCutPlayShot({ imageUrl, grid, paths, svgPathFallback }) {
    const url = puzzleBoardImageUrl(imageUrl);
    if (!url) return null;
    let nextGrid = Number(grid) || 0;
    let nextPaths = Array.isArray(paths) ? paths : [];
    if (nextGrid <= 0 || nextPaths.length === 0) {
        nextGrid = FALLBACK_JIGSAW_GRID;
        nextPaths = FALLBACK_JIGSAW_PIECE_PATHS;
    }
    const layout = layoutForGrid(nextGrid);
    return {
        id: "jigsaw",
        name: "Jigsaw Muse",
        kind: "jigsaw-play",
        src: url,
        imageUrl: url,
        grid: nextGrid,
        paths: nextPaths,
        boardPieceIndices: layout.boardPieceIndices,
        trayPieceIndices: layout.trayPieceIndices,
        svgPathFallback: svgPathFallback || null,
    };
}

const HERO_APPS = [
    {
        id: "jigsaw",
        name: "Jigsaw Muse",
    },
    {
        id: "sliding",
        name: "Sliding Puzzle Quest",
        fallbackSrc: `${PUBLIC}/ios-sliding-snapshots/300x650bb_1.webp`,
    },
    {
        id: "word-wheel",
        name: "Word Wheel Quest",
        fallbackSrc: `${PUBLIC}/bg_images/word-wheel/beach.png`,
    },
];

function emptyJigsawPlayShot() {
    const layout = layoutForGrid(FALLBACK_JIGSAW_GRID);
    return {
        id: "jigsaw",
        name: "Jigsaw Muse",
        kind: "jigsaw-play",
        src: "",
        imageUrl: null,
        grid: FALLBACK_JIGSAW_GRID,
        paths: FALLBACK_JIGSAW_PIECE_PATHS,
        boardPieceIndices: layout.boardPieceIndices,
        trayPieceIndices: layout.trayPieceIndices,
        svgPathFallback: null,
    };
}

/** Always three apps: Jigsaw, Sliding, Word Wheel. Do not use store-icon art as the board. */
export const FALLBACK_HERO_SHOTS = [
    emptyJigsawPlayShot(),
    {
        id: "sliding",
        name: "Sliding Puzzle Quest",
        kind: "sliding-play",
        src: HERO_APPS[1].fallbackSrc,
        imageUrl: HERO_APPS[1].fallbackSrc,
        gridSize: 4,
        layout: DEMO_SLIDING_LAYOUT_4,
        title: "Sliding Puzzle Quest",
    },
    {
        id: "word-wheel",
        name: "Word Wheel Quest",
        kind: "word-wheel-play",
        puzzle: DEMO_WORD_WHEEL_PUZZLE,
        foundWords: DEMO_WORD_WHEEL_FOUND,
    },
];

function firstUrl(...vals) {
    for (const value of vals) {
        if (typeof value === "string" && value.trim()) return value.trim();
    }
    return null;
}

/** Store banners / app icons must not be used as die-cut puzzle photos. */
function puzzleBoardImageUrl(...vals) {
    const url = firstUrl(...vals);
    if (!url) return null;
    if (/jigsaw-muse-feature-graphic/i.test(url) || /jigsaw-muse-app-icon/i.test(url)) {
        return null;
    }
    return url;
}

function withTimeout(promise, ms = FETCH_MS) {
    let timer;
    const timeout = new Promise((_, reject) => {
        timer = setTimeout(() => reject(new Error("timeout")), ms);
    });
    return Promise.race([promise, timeout]).finally(() => clearTimeout(timer));
}

function languageTries(language) {
    const preferred = String(language || "").trim();
    const out = [];
    if (preferred) out.push(preferred);
    if (preferred !== "english") out.push("english");
    out.push("");
    return out;
}

function pickTemplateBoardUrl(templates, preferredSize) {
    if (!Array.isArray(templates) || templates.length === 0) return null;
    const withUrl = templates
        .map((template) => ({
            size: Number(template?.gridSize ?? template?.tileSize ?? 0),
            url: firstUrl(
                template?.imageUrl,
                template?.image_url,
                template?.previewImageUrl,
                template?.preview_image_url,
            ),
        }))
        .filter((row) => row.url);
    if (withUrl.length === 0) return null;
    withUrl.sort((a, b) => Math.abs(a.size - preferredSize) - Math.abs(b.size - preferredSize));
    return withUrl[0].url;
}

function pieceListFromTemplate(template) {
    const paths = template?.piecePaths?.pieces
        ?? template?.piece_paths?.pieces
        ?? template?.piecePaths;
    const list = Array.isArray(paths) ? paths : paths?.pieces;
    return Array.isArray(list) ? list : [];
}

function pickHeroJigsawTemplate(templates) {
    const list = Array.isArray(templates) ? templates : [];
    const withPaths = list.filter((template) => pieceListFromTemplate(template).length > 0);
    for (const size of [6, 9, 12]) {
        const match = withPaths.find((template) => resolveJigsawTemplateTileSize(template) === size);
        if (match) return match;
    }
    return withPaths[0] || pickDefaultJigsawPlayTemplate(list);
}

function jigsawPlayShot(group, template) {
    const imageUrl =
        pickJigsawSquareImageUrl(group?.story)
        || pickJigsawCardImageUrl(group?.story)
        || pickTemplateBoardUrl(group?.templates, 6);
    return dieCutPlayShot({
        imageUrl,
        grid: resolveJigsawTemplateTileSize(template),
        paths: pieceListFromTemplate(template),
        svgPathFallback: template?.svgPath || template?.svg_path || null,
    });
}

async function jigsawPlayFromGroup(group) {
    if (!group) return null;
    const storyId = group.storyId || group.story?.id;
    const hasPaths = (group.templates || []).some((template) => pieceListFromTemplate(template).length > 0);
    let full = group;
    if (storyId && !hasPaths) {
        const raw = await withTimeout(
            ApiService.getData(`/home/jigsaw/${encodeURIComponent(storyId)}`),
        );
        full = normalizeHomeJigsawGroup(raw) ?? raw ?? group;
    }
    return jigsawPlayShot(full, pickHeroJigsawTemplate(full?.templates));
}

function foundWordsFromPuzzle(puzzle, count = 2) {
    const words = String(puzzle?.wordsInUse || "")
        .split(/\n/)
        .map((word) => word.trim().toUpperCase())
        .filter(Boolean);
    if (words.length === 0) return [];
    return words.slice(0, Math.min(count, Math.max(1, words.length - 1)));
}

function pickSlidingTemplateById(group, slidingId) {
    const target = String(slidingId || "").trim().toLowerCase();
    const templates = Array.isArray(group?.templates) ? group.templates : [];
    if (!target) return null;
    return templates.find((template) => String(template?.id ?? "").trim().toLowerCase() === target) ?? null;
}

function gridSizeFromSlidingTemplate(template, fallback = 4) {
    const size = Number(template?.gridSize ?? template?.grid_size);
    if (Number.isFinite(size) && size >= 3) return size;
    const layout = template?.startingLayout ?? template?.starting_layout;
    if (Array.isArray(layout) && layout.length > 0) {
        const side = Math.round(Math.sqrt(layout.length));
        if (side * side === layout.length) return side;
    }
    return fallback;
}

function layoutFromSlidingTemplate(template, gridSize) {
    const n = gridSize * gridSize;
    const raw = template?.startingLayout ?? template?.starting_layout;
    if (Array.isArray(raw) && raw.length === n) {
        const nums = raw.map((value) => Number(value));
        if (nums.every(Number.isFinite) && !nums.every((value, index) => value === index)) {
            return nums;
        }
    }
    return gridSize === 4 ? DEMO_SLIDING_LAYOUT_4 : null;
}

function slidingPlayShot({ imageUrl, gridSize, layout, title }) {
    const url = firstUrl(imageUrl);
    if (!url) return null;
    const gs = Number(gridSize) > 0 ? Number(gridSize) : 4;
    const nextLayout =
        Array.isArray(layout) && layout.length === gs * gs
            ? layout.map((value) => Number(value))
            : gs === 4
              ? DEMO_SLIDING_LAYOUT_4
              : Array.from({ length: gs * gs }, (_, i) => i);
    return {
        id: "sliding",
        name: "Sliding Puzzle Quest",
        kind: "sliding-play",
        src: url,
        imageUrl: url,
        gridSize: gs,
        layout: nextLayout,
        title: title || "Sliding Puzzle Quest",
    };
}

function slidingPlayFromGroup(group, preferredSlidingId) {
    if (!group) return null;
    const templates = Array.isArray(group.templates) ? group.templates : [];
    const template =
        pickSlidingTemplateById(group, preferredSlidingId)
        || templates.find((row) => Number(row?.gridSize ?? row?.grid_size) === 4)
        || templates[0];
    const gridSize = gridSizeFromSlidingTemplate(template, 4);
    return slidingPlayShot({
        imageUrl:
            pickSlidingPlayImageUrl(group.story)
            || pickSlidingCardImageUrl(group.story)
            || pickTemplateBoardUrl(templates, 4),
        gridSize,
        layout: layoutFromSlidingTemplate(template, gridSize),
        title: getSlidingStoryTitle(group.story) || "Sliding Puzzle Quest",
    });
}

async function fetchHeroSlidingContent() {
    const group = await withTimeout(SlidingService.getStoryGroup(HERO_SLIDING_STORY_ID));
    const play = slidingPlayFromGroup(group, HERO_SLIDING_ID);
    if (play) return play;
    const byId = await withTimeout(SlidingService.getById(HERO_SLIDING_ID));
    const data = byId?.data;
    if (!data) return null;
    const gridSize = gridSizeFromSlidingTemplate(data, 4);
    return slidingPlayShot({
        imageUrl:
            pickSlidingPlayImageUrl(data?.story)
            || pickSlidingCardImageUrl(data?.story)
            || firstUrl(data?.imageUrl, data?.image_url),
        gridSize,
        layout: layoutFromSlidingTemplate(data, gridSize),
        title: getSlidingStoryTitle(data?.story) || "Sliding Puzzle Quest",
    });
}

async function fetchPublicJson(path) {
    const result = await apiFetch(resolveApiFetchUrl(path), {
        method: "GET",
        headers: { "Content-Type": "application/json" },
    });
    if (result.status === 204) return null;
    const json = await result.json().catch(() => null);
    if (!json || json.code === "NO_DATA" || json.code === "FAILURE") return null;
    if (!result.ok) return null;
    return json;
}

export async function fetchJigsawHeroShot(language) {
    try {
        for (const lang of languageTries(language)) {
            try {
                const newest = await withTimeout(
                    JigsawService.getNewest({ language: lang || "english", excludeCompleted: false }),
                );
                const play = await jigsawPlayFromGroup(newest?.data);
                if (play) return play;
            } catch {
                /* try browse */
            }
            try {
                const page = await withTimeout(
                    JigsawService.listBrowse({
                        page: 0,
                        pageSize: 8,
                        language: lang,
                        sort: "publishedAt:desc",
                    }),
                );
                for (const row of page?.content ?? []) {
                    const play = await jigsawPlayFromGroup(row);
                    if (play) return play;
                }
            } catch {
                /* next language */
            }
        }
        try {
            const raw = await withTimeout(
                ApiService.getData(`/home/jigsaw/${encodeURIComponent(RECORDING_CONTENT_STORY_ID)}`),
            );
            const play = await jigsawPlayFromGroup(normalizeHomeJigsawGroup(raw) ?? raw);
            if (play) return play;
        } catch {
            /* bundled die-cut fallback */
        }
        return null;
    } catch (error) {
        Logger.warn("landing hero jigsaw shot failed:", error);
        return null;
    }
}

export async function fetchSlidingHeroShot(language) {
    try {
        try {
            const featured = await fetchHeroSlidingContent();
            if (featured) return featured;
        } catch {
            /* try newest published */
        }
        for (const lang of languageTries(language)) {
            try {
                const newest = await withTimeout(
                    SlidingService.getNewest({ language: lang || "english", excludeCompleted: false }),
                );
                const fromNewest = slidingPlayFromGroup(newest?.data);
                if (fromNewest) return fromNewest;
            } catch {
                /* try browse */
            }
            try {
                const page = await withTimeout(
                    SlidingService.listBrowse({
                        page: 0,
                        pageSize: 8,
                        language: lang,
                        sort: "publishedAt:desc",
                    }),
                );
                for (const row of page?.content ?? []) {
                    const found = slidingPlayFromGroup(row);
                    if (found) return found;
                }
            } catch {
                /* next language */
            }
        }
        return null;
    } catch (error) {
        Logger.warn("landing hero sliding shot failed:", error);
        return null;
    }
}

export async function fetchWordWheelHeroShot() {
    try {
        const pinned = await withTimeout(
            fetchPublicJson(`/v1/puzzle/wordwheel/${HERO_WORD_WHEEL_TPL_ID}`),
        );
        const newest = (!pinned || !(pinned.filledCoordinates || pinned.wordsInUse))
            ? await withTimeout(fetchPublicJson("/v1/puzzle/wordwheel/newest"))
            : null;
        const puzzle = (pinned && (pinned.filledCoordinates || pinned.wordsInUse))
            ? pinned
            : newest;
        if (puzzle && (puzzle.filledCoordinates || puzzle.wordsInUse)) {
            return {
                id: "word-wheel",
                name: "Word Wheel Quest",
                kind: "word-wheel-play",
                puzzle,
                foundWords: [],
            };
        }
        return null;
    } catch (error) {
        Logger.warn("landing hero word-wheel shot failed:", error);
        return null;
    }
}

/** Always returns three shots, one per Android app. */
export async function fetchLandingHeroShots(language) {
    const [jigsaw, sliding, wordWheel] = await Promise.all([
        fetchJigsawHeroShot(language),
        fetchSlidingHeroShot(language),
        fetchWordWheelHeroShot(),
    ]);
    return [
        jigsaw || FALLBACK_HERO_SHOTS[0],
        sliding || FALLBACK_HERO_SHOTS[1],
        wordWheel || FALLBACK_HERO_SHOTS[2],
    ];
}
